// File: typescript/apps/front-end/src/main/react/board/BoardCardBlockLazy.tsx
//
// Lazy boundary for the feed card's board block (design §18.2).
//
// `DiscussionCardDisplayer` is on the eager startup path — it renders on the home feed, which is the
// page the CWV work exists to protect. Importing `BoardCardBlock` directly there would put the board
// reading module, the vote control and the client wrapper into the startup parse for every visitor, to
// serve a facet most posts do not have. Routed through `lazyWithRetry` per rule 26, so a chunk fetch
// that fails retries and then reloads instead of leaving a dead mount.
//
// The real gate is upstream and cheaper than either: the caller renders this only once the summaries
// fetch has produced a board for that card, so a feed with no boards on it never requests the chunk.

import React, { Suspense } from "react";

import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { LazyControlBoundary } from "@/main/react/LazyControlBoundary";
import type { Localizer } from "@/main/react/Types";
import type { BoardCardSummary } from "@/main/react/board/boardView";

const BoardCardBlockImpl = lazyWithRetry(
    () => import("@/main/react/board/BoardCardBlock"),
    "board-card-block",
);

export type BoardCardBlockLazyProps = {
    localizer: Localizer;
    discussionPublicId: string;
    discussionHref: string;
    summary: BoardCardSummary;
};

/**
 * No fallback, deliberately. Rule 32's client-side form: this block is ADDITIVE to a card that is
 * already complete without it, so the honest loading state is the card the reader already has. A
 * skeleton here would reserve space on a card that may never fill it.
 */
export default function BoardCardBlockLazy(props: BoardCardBlockLazyProps) {
    // Containment (FI-070, 2026-09-15): a chunk that never arrives costs this control, not the island
    // around it. The boundary sits OUTSIDE the Suspense, where the lazy rejection is re-thrown to. `null`
    // is the honest fallback for the same reason it is the Suspense fallback: what the member already had
    // is complete without this, and the failure is logged and marked by the boundary itself.
    return (
        <LazyControlBoundary controlName="board-card-block" fallback={null}>
            <Suspense fallback={null}>
                <BoardCardBlockImpl {...props} />
            </Suspense>
        </LazyControlBoundary>
    );
}
